/**
 * Chat Header - WhatsApp-style header for conversation
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Typography, Avatar, Dropdown, Button, Modal, List, Input, Select, Popconfirm, message as antMessage } from 'antd';
import { MoreOutlined, UserOutlined, ArrowLeftOutlined, BellOutlined, BellFilled, EditOutlined, DeleteOutlined, CrownOutlined, UserAddOutlined, UserDeleteOutlined, ReadOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../config/api';
import { useChatWebSocket } from '../../contexts/ChatWebSocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { PresenceIndicator } from './PresenceIndicator';
import './ChatHeader.css';

const { Text } = Typography;

const MUTE_STORAGE_KEY = 'chat-muted-channels';

const getMutedChannels = (): Set<string> => {
  try {
    const raw = localStorage.getItem(MUTE_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};

const setMutedChannel = (channelId: string, muted: boolean) => {
  try {
    const set = getMutedChannels();
    if (muted) set.add(channelId);
    else set.delete(channelId);
    localStorage.setItem(MUTE_STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch (_) {}
};

type PresenceStatus = 'online' | 'away' | 'busy' | 'offline';

const normalizeStatus = (s: string | undefined): PresenceStatus =>
  ['online', 'away', 'busy', 'offline'].includes(s || '') ? (s as PresenceStatus) : 'offline';

interface ChatHeaderProps {
  channelId: string;
  onBackToList?: () => void;
  showBackButton?: boolean;
  newsModalVisible?: boolean;
  onNewsModalOpen?: () => void;
  onNewsModalClose?: () => void;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({ channelId, onBackToList, showBackButton, newsModalVisible, onNewsModalOpen, onNewsModalClose }) => {
  const { presence } = useChatWebSocket();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [membersModalVisible, setMembersModalVisible] = useState(false);
  const [mutedChannels, setMutedChannels] = useState<Set<string>>(getMutedChannels);
  const [renameVisible, setRenameVisible] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [inviting, setInviting] = useState(false);

  const { data } = useQuery(
    ['channel-details', channelId],
    () => api.get(`/api/chat/channels/${channelId}`).then(res => res.data),
    { enabled: !!channelId }
  );

  const { data: membersData } = useQuery(
    ['channel-members', channelId],
    () => api.get(`/api/chat/channels/${channelId}/members`).then(res => res.data),
    { enabled: !!channelId }
  );

  const { data: allUsersData } = useQuery(
    'chat-all-users',
    () => api.get('/api/chat/users').then(res => res.data),
    { enabled: !!channelId && inviteModalVisible }
  );

  const channel = data?.channel;
  const members = membersData?.members || [];
  const isMuted = mutedChannels.has(channelId);

  const handleToggleMute = useCallback(() => {
    const next = !isMuted;
    setMutedChannel(channelId, next);
    setMutedChannels(getMutedChannels());
    antMessage.success(next ? 'Notifications muted for this group' : 'Notifications enabled for this group');
    // Dispatch custom event for useChatNotifications to respect mute
    window.dispatchEvent(new CustomEvent('chat-mute-changed', { detail: { channelId, muted: next } }));
  }, [channelId, isMuted]);

  const getDisplayName = () => {
    if (!channel) return 'Chat';
    if (channel.type === 'direct') {
      const other = members.find((m: any) => m.id !== channel.created_by);
      if (other) {
        const fn = other.first_name || other.firstName || '';
        const ln = other.last_name || other.lastName || '';
        return `${fn} ${ln}`.trim() || other.email || 'Contact';
      }
      const ou = channel.other_user || channel.otherUser;
      if (ou) {
        const fn = ou.first_name || (ou as any).firstName || '';
        const ln = ou.last_name || (ou as any).lastName || '';
        return `${fn} ${ln}`.trim() || ou.email || 'Contact';
      }
    }
    return channel.name || 'Group';
  };

  const getOtherMember = () => {
    if (channel?.type !== 'direct') return null;
    const other = members.find((m: any) => m.id !== channel.created_by);
    if (other) return other;
    return channel.other_user || channel.otherUser;
  };

  const otherMember = getOtherMember();
  const status = otherMember
    ? normalizeStatus(presence[otherMember.id]?.status || otherMember.presence_status || (otherMember as any).presenceStatus)
    : 'offline';

  const getMemberStatus = (member: any) => {
    const userPresence = presence[member.id] || null;
    return userPresence?.status
      ? normalizeStatus(userPresence.status)
      : normalizeStatus(member.presence_status || member.presenceStatus);
  };

  const isAdmin = user?.role === 'admin';
  const currentMember = members.find((m: any) => m.id === user?.id);
  const isChannelAdmin = currentMember?.role === 'admin';
  const canAdminChannel = isAdmin || isChannelAdmin;
  const isCreator = channel?.created_by === user?.id;
  const canDeleteChannel = isAdmin || isCreator || isChannelAdmin;

  useEffect(() => {
    if (renameVisible && channel) {
      setRenameValue(channel.name || '');
    }
  }, [renameVisible, channel]);

  const handleRenameChannel = async () => {
    if (!renameValue.trim()) {
      antMessage.error('Group name cannot be empty');
      return;
    }
    if (renameValue.trim() === channel?.name) {
      setRenameVisible(false);
      return;
    }
    setRenaming(true);
    try {
      await api.put(`/api/chat/channels/${channelId}`, { name: renameValue.trim() });
      queryClient.invalidateQueries(['channel-details', channelId]);
      queryClient.invalidateQueries('chat-channels');
      antMessage.success('Group renamed successfully');
      setRenameVisible(false);
    } catch (error: any) {
      antMessage.error(error.response?.data?.error || 'Failed to rename channel');
    } finally {
      setRenaming(false);
    }
  };

  const handleDeleteChannel = async () => {
    try {
      await api.delete(`/api/chat/channels/${channelId}`);
      antMessage.success(channel?.type === 'direct' ? 'Direct message deleted' : 'Group archived');
      queryClient.invalidateQueries('chat-channels');
      setDeleteConfirmVisible(false);
      if (onBackToList) onBackToList();
      else window.location.reload();
    } catch (error: any) {
      antMessage.error(error.response?.data?.error || 'Failed to delete channel');
    }
  };

  const handleUpdateMemberRole = async (memberId: string, newRole: 'admin' | 'member') => {
    setUpdatingRole(memberId);
    try {
      await api.put(`/api/chat/channels/${channelId}/members/${memberId}/role`, { role: newRole });
      queryClient.invalidateQueries(['channel-members', channelId]);
      queryClient.invalidateQueries('chat-channels');
      antMessage.success(newRole === 'admin' ? 'Member promoted to group admin' : 'Member demoted to regular member');
    } catch (error: any) {
      antMessage.error(error.response?.data?.error || 'Failed to update member role');
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    setRemovingMemberId(memberId);
    try {
      await api.post(`/api/chat/channels/${channelId}/members/remove`, { userIds: [memberId] });
      queryClient.invalidateQueries(['channel-members', channelId]);
      queryClient.invalidateQueries('chat-channels');
      antMessage.success(memberId === user?.id ? 'You left the group' : 'Member removed');
      if (memberId === user?.id && onBackToList) {
        setMembersModalVisible(false);
        onBackToList();
      } else {
        setMembersModalVisible(false);
      }
    } catch (error: any) {
      antMessage.error(error.response?.data?.error || 'Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleInviteUsers = async () => {
    if (selectedUserIds.length === 0) {
      antMessage.warning('Please select at least one user');
      return;
    }
    setInviting(true);
    try {
      await api.post(`/api/chat/channels/${channelId}/members`, { userIds: selectedUserIds });
      queryClient.invalidateQueries(['channel-members', channelId]);
      queryClient.invalidateQueries('chat-channels');
      antMessage.success(`Invited ${selectedUserIds.length} user(s) to the group`);
      setInviteModalVisible(false);
      setSelectedUserIds([]);
    } catch (error: any) {
      antMessage.error(error.response?.data?.error || 'Failed to invite users');
    } finally {
      setInviting(false);
    }
  };

  const allUsers = allUsersData?.users || [];
  const availableUsers = allUsers.filter((u: any) => !members.some((m: any) => m.id === u.id));

  const menuItems = [
    {
      key: 'view-members',
      icon: <UserOutlined />,
      label: `View members (${members.length})`,
      onClick: () => setMembersModalVisible(true),
    },
    ...(channel?.type !== 'direct' && onNewsModalOpen
      ? [
          {
            key: 'news',
            icon: <ReadOutlined />,
            label: 'News Feed',
            onClick: onNewsModalOpen,
          },
        ]
      : []),
    {
      key: 'mute',
      icon: isMuted ? <BellFilled /> : <BellOutlined />,
      label: isMuted ? 'Unmute notifications' : 'Mute notifications',
      onClick: handleToggleMute,
    },
    ...(canAdminChannel && channel?.type !== 'direct' ? [
      {
        key: 'rename',
        icon: <EditOutlined />,
        label: 'Rename group',
        onClick: () => setRenameVisible(true),
      },
    ] : []),
    ...(canDeleteChannel ? [
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: channel?.type === 'direct' ? 'Delete conversation' : 'Archive group',
        danger: true,
        onClick: () => setDeleteConfirmVisible(true),
      },
    ] : []),
  ];

  return (
    <div className="whatsapp-chat-header">
      {showBackButton && onBackToList && (
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          className="chat-header-back-btn"
          onClick={onBackToList}
          aria-label="Back to conversations"
        />
      )}
      <div className="chat-header-avatar">
        {channel?.type === 'direct' && otherMember ? (
          <PresenceIndicator status={status} size="small">
            <Avatar size={40} icon={<UserOutlined />} className="header-avatar" />
          </PresenceIndicator>
        ) : (
          <Avatar size={40} icon={<UserOutlined />} className="header-avatar group-avatar" />
        )}
      </div>
      <div className="chat-header-info">
        <Text strong className="chat-header-name">
          {getDisplayName()}
        </Text>
        <Text className="chat-header-status">
          {channel?.type === 'direct'
            ? status === 'online'
              ? 'Online'
              : status === 'away'
              ? 'Away'
              : status === 'busy'
              ? 'Busy'
              : 'Offline'
            : `${members.length} members`}
        </Text>
      </div>
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        placement="bottomRight"
      >
        <button type="button" className="chat-header-menu-btn" aria-label="Channel options">
          <MoreOutlined />
        </button>
      </Dropdown>

      <Modal
        title={channel?.type === 'direct' ? 'Conversation' : 'Group Members'}
        open={membersModalVisible}
        onCancel={() => setMembersModalVisible(false)}
        footer={null}
        width={Math.min(520, window.innerWidth - 32)}
        className="chat-members-modal"
      >
        {channel?.type !== 'direct' && canAdminChannel && (
          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              size="small"
              onClick={() => setInviteModalVisible(true)}
            >
              Invite
            </Button>
          </div>
        )}
        <List
          dataSource={members}
          renderItem={(member: any) => {
            const fn = member.first_name || member.firstName || '';
            const ln = member.last_name || member.lastName || '';
            const displayName = `${fn} ${ln}`.trim() || member.email || 'Unknown';
            const memberStatus = getMemberStatus(member);
            const memberRole = member.role || 'member';
            const isMemberAdmin = memberRole === 'admin';
            const isCurrentUser = member.id === user?.id;
            const showPromoteDemote = canAdminChannel && channel?.type !== 'direct' && !isCurrentUser;
            const canRemove = channel?.type !== 'direct' && (canAdminChannel || isCurrentUser);
            const showRemove = canRemove && (isCurrentUser || canAdminChannel);
            return (
              <List.Item
                actions={
                  channel?.type !== 'direct'
                    ? [
                        ...(showPromoteDemote
                          ? [
                              isMemberAdmin ? (
                                <Button
                                  key="demote"
                                  type="link"
                                  size="small"
                                  loading={updatingRole === member.id}
                                  onClick={() => handleUpdateMemberRole(member.id, 'member')}
                                >
                                  Demote
                                </Button>
                              ) : (
                                <Button
                                  key="promote"
                                  type="link"
                                  size="small"
                                  loading={updatingRole === member.id}
                                  onClick={() => handleUpdateMemberRole(member.id, 'admin')}
                                >
                                  Make admin
                                </Button>
                              ),
                            ]
                          : []),
                        ...(showRemove
                          ? [
                              <Popconfirm
                                key="remove"
                                title={isCurrentUser ? 'Leave this group?' : `Remove ${displayName}?`}
                                onConfirm={() => handleRemoveMember(member.id)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button
                                  type="link"
                                  size="small"
                                  danger
                                  loading={removingMemberId === member.id}
                                  icon={<UserDeleteOutlined />}
                                >
                                  {isCurrentUser ? 'Leave' : 'Remove'}
                                </Button>
                              </Popconfirm>,
                            ]
                          : []),
                      ]
                    : undefined
                }
              >
                <List.Item.Meta
                  avatar={
                    <PresenceIndicator status={memberStatus} size="small">
                      <Avatar size={40} src={member.avatar_url} icon={<UserOutlined />} />
                    </PresenceIndicator>
                  }
                  title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {displayName}
                      {isMemberAdmin && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <CrownOutlined style={{ color: '#faad14' }} />
                          <span style={{ fontSize: 12, color: '#faad14' }}>Admin</span>
                        </span>
                      )}
                      {isCurrentUser && (
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>(you)</span>
                      )}
                    </span>
                  }
                  description={
                    <span className="chat-member-email" title={member.email}>
                      {member.email}
                    </span>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Modal>

      <Modal
        title="Invite Users to Group"
        open={inviteModalVisible}
        onOk={handleInviteUsers}
        onCancel={() => {
          setInviteModalVisible(false);
          setSelectedUserIds([]);
        }}
        okText="Invite"
        confirmLoading={inviting}
      >
        <Select
          mode="multiple"
          placeholder="Select users to invite"
          value={selectedUserIds}
          onChange={setSelectedUserIds}
          style={{ width: '100%' }}
          showSearch
          filterOption={(input, option) => {
            const label = typeof option?.label === 'string' ? option.label : String(option?.label || '');
            return label.toLowerCase().includes(input.toLowerCase());
          }}
          options={availableUsers.map((u: any) => {
            const fn = u.first_name || u.firstName || '';
            const ln = u.last_name || u.lastName || '';
            const displayName = `${fn} ${ln}`.trim() || u.email || 'Unknown';
            return { label: displayName, value: u.id };
          })}
        />
      </Modal>

      <Modal
        title="Rename Group"
        open={renameVisible}
        onOk={handleRenameChannel}
        onCancel={() => setRenameVisible(false)}
        okText="Rename"
        confirmLoading={renaming}
      >
        <Input
          placeholder="Enter new group name"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onPressEnter={handleRenameChannel}
          maxLength={255}
          autoFocus
        />
      </Modal>

      <Modal
        title={channel?.type === 'direct' ? 'Delete conversation' : 'Archive group'}
        open={deleteConfirmVisible}
        onOk={handleDeleteChannel}
        onCancel={() => setDeleteConfirmVisible(false)}
        okText={channel?.type === 'direct' ? 'Delete' : 'Archive'}
        okButtonProps={{ danger: true }}
      >
        <p>
          {channel?.type === 'direct'
            ? 'Are you sure you want to delete this conversation? It will be archived and removed from your list.'
            : 'Are you sure you want to archive this group? It will be hidden from the group list.'}
        </p>
      </Modal>
    </div>
  );
};
